---
title: "The prospect experience timeline"
description: "Every public role a sourced prospect held, in order with dates, plus auto-surfaced career signals — a sanity check before you spend effort on outreach."
category: "sourcing"
keywords:
  - "prospect experience timeline"
  - "candidate work history"
  - "career signals"
  - "sourcing pre-outreach check"
  - "prospect profile"
schema: "HowTo"
nav:
  section: "guides"
  order: 50
  parent: "/guides/sourcing"
---

Every sourced prospect has an experience timeline: each public role Chosen could assemble, in order, with dates. Above it sits a panel of signals Chosen reads off that history automatically — "promoted from IC to manager in two years", "switched industries three times". Together they're a fast sanity check before you spend personalization effort on someone.

## What it is

When Chosen surfaces a prospect from a [Mission](/guides/sourcing/find-prospects), it assembles their work history from public profiles. The **Experience** section of the prospect drawer renders that history as a timeline — company, title, location, and dates for each role, current job marked. It's the same source data behind the prospect's headline, shown in full instead of summarized.

The point of seeing it laid out: a one-line headline ("Senior Engineer at Stripe") hides the things that decide whether outreach is worth sending. The timeline shows them.

## Read the auto-surfaced signals

Chosen reads the timeline and surfaces patterns on its own, in the **Key insights** panel of the drawer. Signals come in three flavors:

- **Positive** — something that supports the fit. "Steady three-year tenures", "deep specialization in one domain".
- **Caution** — something worth a closer look before you commit. "Three roles in two years", "a recent industry switch".
- **Negative** — something that likely works against the fit for this role.

A caution signal is not a rejection. It's Chosen pointing at the part of the history you'd want to have read anyway — so you read it now, before drafting, instead of after a reply.

## Use it as a pre-outreach check

Open the timeline before you write the [draft](/guides/sourcing/outreach-drafts), not after. A minute here decides whether the next ten are worth spending.

<Steps>
  <li>Open the prospect and scroll to **Experience**.</li>
  <li>Check the story holds up — does the work history actually match the role you're sourcing for, or just the current job title?</li>
  <li>Read the **Key insights** signals. A caution flag is a prompt to look closer, not a reason to skip.</li>
  <li>Look at tenure and trajectory. Someone four months into a new job is a different outreach than someone three years in and visibly restless.</li>
  <li>If it holds up, draft your outreach — and use what you saw to make it specific.</li>
</Steps>

<Callout type="tip" title="The timeline is also your personalization material">
The detail that makes an outreach land — a specific past company, a notable jump in scope, a domain they clearly know — is sitting in the timeline. Reading it does double duty: it's the fit check and the source of the one true sentence that makes your message worth a reply.
</Callout>

## When it gets it wrong

The timeline is assembled from public profiles, so it inherits their gaps. A role the prospect never posted publicly won't appear. Dates can be approximate, and a contract or part-time stint can read like a full-time job. Signals are pattern-matching on imperfect data — "job hopper" might just be a string of acquisitions, and "industry switcher" might be one company that's hard to categorize.

Treat the timeline as a strong lead, not a verified CV. It's accurate enough to catch the prospects who clearly don't fit and to point you at the detail worth mentioning — which is most of the value, before a single email goes out. When a prospect is worth pursuing, the next step is [drafting outreach](/guides/sourcing/outreach-drafts).
